import React, {
  ElementType,
  FC,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import styles from "./ChatMessage.module.css";
import Colors from "../../ui-kit/colors";
import { FlexRow } from "../Layout/Flex";
import { Transition } from "react-transition-group";
import Container from "../../ui-kit/Layout/Container";
import ResponsiveGutter from "../ResponsiveGutter";
import classNames from "classnames";
import { Typography } from "ui-kit/Typography";

type ChatContainerProps = {
  children: ReactNode | ReactNode[];
  leaving?: boolean;
};

export const CHAT_MESSAGE_DISPLAY_NAME = "ChatMessage";

export const ChatContainer = ({
  children,
  leaving,
}: ChatContainerProps): ReactElement => {
  let indexRef = useRef(-1);
  indexRef.current = -1;
  return (
    <Container style={{ contain: "paint", paddingTop: 24 }}>
      {React.Children.map(children, (child, index) => {
        // @ts-ignore
        if (child?.type?.displayName === CHAT_MESSAGE_DISPLAY_NAME) {
          indexRef.current += 1;

          // @ts-ignore
          return (
            // @ts-ignore
            <child.type
              // @ts-ignore
              {...child.props}
              animated={true}
              transitioning={leaving}
              index={indexRef.current}
            />
          );
        }
        return child;
      })}
    </Container>
  );
};

type ChatMessageType = {
  children: ReactNode;
  unSent?: boolean;
  fromMe?: boolean;
  index?: number;
  small?: boolean;
  transitioning?: boolean;
  blink?: boolean;
  onPress?: () => void;
  fullWidth?: boolean;
  backgroundColor?: keyof typeof Colors;
  borderColor?: keyof typeof Colors;
  border?: boolean;
  gutters?: boolean;
  active?: boolean;
  style?: any;
  component?: ElementType;
};

const DURATION = 300;

export const MessageTransition = ({
  transitioning,
  index = 1,
  fromMe,
  children,
  className,
}: {
  transitioning?: boolean;
  index?: number;
  fromMe?: boolean;
  children: ReactNode;
  className?: string;
}) => {
  const enterDelay = index * 100;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (transitioning) {
      setVisible(false);
    } else {
      setVisible(true);
    }
  }, [transitioning]);

  const defaultStyle = {
    transition: `all ${DURATION}ms ease-out ${enterDelay}ms`,
    opacity: 0,
    transform: fromMe ? "translateX(25px)" : "translateX(-25px)",
  };

  const transitionStyles = {
    entering: { opacity: 0 },
    entered: { opacity: 1, transform: undefined },
    exiting: { opacity: 0 },
    exited: { opacity: 0 },
    unmounted: {},
  };

  return (
    // @ts-ignore
    <Transition in={visible} timeout={DURATION}>
      {(state) => {
        return (
          <div
            style={{
              ...defaultStyle,
              ...transitionStyles[state],
            }}
            className={className}
          >
            {children}
          </div>
        );
      }}
    </Transition>
  );
};

const ChatMessage: FC<ChatMessageType> = ({
  blink,
  index,
  small = false,
  children,
  unSent,
  fromMe,
  transitioning,
  onPress,
  fullWidth,
  backgroundColor,
  borderColor,
  border = true,
  gutters = true,
  active,
  style,
  component = "div",
}) => {
  const Component = component;

  return (
    <MessageTransition
      transitioning={transitioning}
      fromMe={fromMe}
      index={index}
    >
      <>
        {unSent ? <ResponsiveGutter mobile={1} tablet={2} /> : null}
        <FlexRow
          justifyContent={fromMe ? "flex-end" : "flex-start"}
          style={style}
        >
          <div
            className={classNames(
              styles.container,
              fullWidth && styles.fullWidth
            )}
          >
            <Component
              onTouchStart={() => {}}
              onClick={() => {
                onPress && onPress();
              }}
              {...(component === "button" && active
                ? { "aria-pressed": "true" }
                : {})}
              className={classNames(
                styles.message,
                blink && styles.blinking,
                !fromMe ? styles.messageFromOther : styles.messageFromMe,
                onPress && styles.clickable,
                fullWidth && styles.fullWidth,
                small && gutters && styles.messageSmall, // cant use small in conjunction with gutters
                gutters && styles.messageGutters,
                active && styles.active
              )}
              style={{
                background: backgroundColor
                  ? Colors[backgroundColor]
                  : fromMe
                  ? undefined
                  : Colors.grey100,
                borderColor: borderColor ? Colors[borderColor] : undefined,
                borderWidth: border ? undefined : 0,
              }}
            >
              <Typography>{children}</Typography>
            </Component>
          </div>
        </FlexRow>
        <ResponsiveGutter mobile={unSent ? 2 : 1} />
      </>
    </MessageTransition>
  );
};

// Used in ChatContainer
ChatMessage.displayName = CHAT_MESSAGE_DISPLAY_NAME;

ChatMessage.defaultProps = {
  unSent: false,
  fromMe: false,
  blink: false,
  onPress: undefined,
  fullWidth: false,
  active: false,
  style: {},
};

export default ChatMessage;
